import { type RefObject, useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { getNodesBounds, useNodes, useReactFlow } from "@xyflow/react";

import { graphMinZoom } from "./ruleGraphViewport/graphMinZoom";
import { RULE_GRAPH_FIT_PADDING } from "./ruleGraphViewport/RULE_GRAPH_FIT_PADDING";

export function RuleGraphViewportSync({
  canvasRef,
  ready,
  revision,
  graphKey,
  minZoom,
  onMinZoom,
  positions,
  bounds,
  onApplied,
}: {
  canvasRef: RefObject<HTMLDivElement | null>;
  ready: boolean;
  revision: string;
  graphKey: string;
  minZoom: number;
  onMinZoom: (zoom: number) => void;
  positions: Map<string, { x: number; y: number }> | undefined;
  bounds: { x: number; y: number; width: number; height: number } | undefined;
  onApplied: (revision: string) => void;
}) {
  const { fitBounds } = useReactFlow();
  const nodes = useNodes();
  const [size, setSize] = useImmer({ width: 0, height: 0 });
  const fitted = useRef<string | null>(null);
  const visible = useRef(false);
  useEffect(() => {
    const canvas = canvasRef.current?.querySelector<HTMLElement>(".react-flow");
    if (!canvas) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(canvas);
    setSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    return () => observer.disconnect();
  }, [canvasRef, setSize]);
  useEffect(() => {
    if (size.width <= 0 || size.height <= 0) {
      visible.current = false;
      return;
    }
    if (!ready || !positions || !bounds || nodes.length !== positions.size) {
      return;
    }
    if (
      nodes.some((node) => {
        const point = positions.get(node.id);
        return (
          !point || point.x !== node.position.x || point.y !== node.position.y
        );
      })
    ) {
      return;
    }
    const next = graphMinZoom(bounds, size);
    onMinZoom(next);
    if (next !== minZoom) {
      return;
    }
    if (!visible.current || fitted.current !== graphKey) {
      void fitBounds(bounds, {
        padding: RULE_GRAPH_FIT_PADDING,
      }).then(() => onApplied(revision));
      fitted.current = graphKey;
      visible.current = true;
    } else {
      onApplied(revision);
    }
  }, [
    ready,
    revision,
    graphKey,
    minZoom,
    size,
    onMinZoom,
    fitBounds,
    nodes,
    positions,
    bounds,
    onApplied,
  ]);
  return null;
}
